/**
 * Single-effeh game widget. Free-text inputs + progressive hints + lock-on-correct.
 * Used by both the daily mode (one effeh per visit) and the endless mode
 * (5 effehat per round). Calls /api/guess directly.
 */

import { useEffect, useMemo, useState } from 'react'
import { ComboBox } from './ComboBox'
import { useOptions } from '../lib/options'

export type EffehInput = {
  id: string
  line_ar: string
  line_translit: string | null
  line_en: string | null
  context_ar: string | null
  has_frame: boolean
}

export type Truth = {
  movie_title_ar: string
  movie_year: number
  movie_kind: string
  actor_name_ar: string
  character_name_ar: string | null
  source_url: string
}

export type EffehResult = {
  /** Movie points only (0-100). Headline score is anchored here. */
  movieScore: number
  /** Actor bonus points (0-50). Shown additively, never in the denominator. */
  bonusScore: number
  /** movieScore + bonusScore — convenience total. */
  totalScore: number
  movieAttempt: number | null   // attempt# when movie became correct, null if never
  actorAttempt: number | null
  outOfAttempts: boolean
  truth: Truth | null
}

type Hint = { attempt: number; text_ar: string }

type GuessResponse = {
  attempt: number
  max_attempts: number
  movie_correct: boolean | null
  actor_correct: boolean | null
  done: boolean
  out_of_attempts: boolean
  row_score: number
  hint: Hint | null
  truth: Truth | null
}

const KIND_AR: Record<string, string> = { film: 'فيلم', series: 'مسلسل', play: 'مسرحية' }

export function EffehGame({
  effeh,
  puzzleKey,
  maxAttempts = 5,
  onDone,
}: {
  effeh: EffehInput
  /** Unique key for analytics (e.g. puzzle_date for daily, round_id for endless). */
  puzzleKey: string
  maxAttempts?: number
  onDone: (result: EffehResult) => void
}) {
  const [movieGuess, setMovieGuess] = useState('')
  const [actorGuess, setActorGuess] = useState('')
  const [movieLockedAttempt, setMovieLockedAttempt] = useState<number | null>(null)
  const [actorLockedAttempt, setActorLockedAttempt] = useState<number | null>(null)
  const [attempt, setAttempt] = useState(1)
  const [hints, setHints] = useState<Hint[]>([])
  const [done, setDone] = useState(false)
  const [outOfAttempts, setOutOfAttempts] = useState(false)
  const [truth, setTruth] = useState<Truth | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [shakeKey, setShakeKey] = useState(0)
  const [startTs] = useState(() => Date.now())
  const options = useOptions()

  // Reset when the effeh changes (endless mode advances to a new one)
  useEffect(() => {
    setMovieGuess('')
    setActorGuess('')
    setMovieLockedAttempt(null)
    setActorLockedAttempt(null)
    setAttempt(1)
    setHints([])
    setDone(false)
    setOutOfAttempts(false)
    setTruth(null)
    setShakeKey(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effeh.id])

  const movieScore = useMemo(() => {
    if (movieLockedAttempt == null) return 0
    return Math.max(0, 100 - 20 * (movieLockedAttempt - 1))
  }, [movieLockedAttempt])

  const bonusScore = useMemo(() => {
    if (actorLockedAttempt == null) return 0
    return Math.max(0, 50 - 10 * (actorLockedAttempt - 1))
  }, [actorLockedAttempt])

  const totalScore = movieScore + bonusScore

  async function submit() {
    if (done || submitting) return
    // Movie is required to submit; actor is optional (bonus only).
    if (movieLockedAttempt == null && !movieGuess.trim()) return

    setSubmitting(true)
    try {
      // Actor payload: null if locked from earlier OR if user left it empty.
      // Server treats both the same (no bonus this attempt, no checking).
      const actorPayload = actorLockedAttempt != null
        ? null
        : (actorGuess.trim() || null)
      const res = await fetch('/api/guess', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          puzzle_date: puzzleKey,
          effeh_id: effeh.id,
          attempt,
          movie_guess: movieLockedAttempt != null ? null : movieGuess.trim(),
          actor_guess: actorPayload,
          ms_taken: Date.now() - startTs,
        }),
      })
      const data = (await res.json()) as GuessResponse

      let newMovieAttempt = movieLockedAttempt
      let newActorAttempt = actorLockedAttempt
      if (data.movie_correct === true && movieLockedAttempt == null) {
        newMovieAttempt = attempt
        setMovieLockedAttempt(attempt)
      }
      if (data.actor_correct === true && actorLockedAttempt == null) {
        newActorAttempt = attempt
        setActorLockedAttempt(attempt)
      }
      if (data.hint) setHints((h) => [...h, data.hint!])

      if (data.done) {
        setDone(true)
        setOutOfAttempts(data.out_of_attempts)
        setTruth(data.truth)
        const movie = newMovieAttempt == null ? 0 : Math.max(0, 100 - 20 * (newMovieAttempt - 1))
        const bonus = newActorAttempt == null ? 0 : Math.max(0, 50 - 10 * (newActorAttempt - 1))
        onDone({
          movieScore: movie,
          bonusScore: bonus,
          totalScore: movie + bonus,
          movieAttempt: newMovieAttempt,
          actorAttempt: newActorAttempt,
          outOfAttempts: data.out_of_attempts,
          truth: data.truth,
        })
      } else {
        setShakeKey((k) => k + 1)
        setAttempt((a) => a + 1)
      }
    } finally {
      setSubmitting(false)
    }
  }

  function giveUp() {
    if (done || submitting) return
    if (!confirm('متأكد إنك عاوز تستسلم؟ هتشوف الإجابة بدون نقط.')) return
    setSubmitting(true)
    fetch('/api/guess', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        puzzle_date: puzzleKey,
        effeh_id: effeh.id,
        attempt: maxAttempts,
        movie_guess: movieLockedAttempt != null ? null : '',
        actor_guess: actorLockedAttempt != null ? null : '',
        ms_taken: Date.now() - startTs,
      }),
    })
      .then((r) => r.json() as Promise<GuessResponse>)
      .then((data) => {
        setDone(true)
        setOutOfAttempts(true)
        setTruth(data.truth)
        setAttempt(maxAttempts)
        const movie = movieLockedAttempt == null ? 0 : Math.max(0, 100 - 20 * (movieLockedAttempt - 1))
        const bonus = actorLockedAttempt == null ? 0 : Math.max(0, 50 - 10 * (actorLockedAttempt - 1))
        onDone({
          movieScore: movie,
          bonusScore: bonus,
          totalScore: movie + bonus,
          movieAttempt: movieLockedAttempt,
          actorAttempt: actorLockedAttempt,
          outOfAttempts: true,
          truth: data.truth,
        })
      })
      .finally(() => setSubmitting(false))
  }

  const remaining = Math.max(0, maxAttempts - attempt + 1)
  const movieLocked = movieLockedAttempt != null
  const actorLocked = actorLockedAttempt != null
  // Movie is the only required field. Actor is optional (bonus only).
  const canSubmit = !done && !submitting
    && (!movieLocked ? movieGuess.trim().length > 0 : true)

  return (
    <div>
      {/* The effeh */}
      <div className="rounded-2xl border border-ink-700/60 bg-ink-700/20 p-8 md:p-12 mb-6 effeh-fade-up">
        {effeh.context_ar && (
          <p className="text-sm text-ink-500 mb-4 text-center">{effeh.context_ar}</p>
        )}
        <div className="mx-auto w-12 effeh-rule mb-5 opacity-60" />
        <p className="effeh-quote text-3xl md:text-5xl leading-relaxed text-center font-display text-ink-50">
          {effeh.line_ar}
        </p>
        <div className="mx-auto w-12 effeh-rule mt-5 opacity-60" />
      </div>

      {!done && (
        <div className="mb-5 text-center">
          <div className="inline-flex gap-1.5 mb-3">
            {Array.from({ length: maxAttempts }).map((_, i) => {
              const n = i + 1
              const used = n < attempt
              const current = n === attempt
              return (
                <span
                  key={n}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    used ? 'bg-rose-600/70' : current ? 'bg-amber-500' : 'bg-ink-700'
                  }`}
                />
              )
            })}
          </div>
          <div className="text-xs text-ink-500">{remaining} محاولة باقية</div>
        </div>
      )}

      {hints.length > 0 && !done && (
        <div className="mb-6 space-y-2 effeh-fade-up">
          {hints.map((h) => (
            <div
              key={h.attempt}
              className="rounded-xl border border-ink-700/60 bg-ink-700/20 px-4 py-3 text-sm text-ink-100"
            >
              <span className="text-xs uppercase tracking-[0.2em] text-amber-500 ms-1">
                تلميحة {numAr(h.attempt)}
              </span>{' '}
              <span className="text-ink-500 mx-1">·</span>{' '}
              {h.text_ar}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3 mb-6" key={shakeKey}>
        <ComboBox
          label="الفيلم / المسلسل"
          options={options?.movies ?? []}
          value={movieGuess}
          onChange={setMovieGuess}
          onSubmit={submit}
          locked={movieLocked}
          truthIfDone={done ? truth?.movie_title_ar : null}
          shake={shakeKey > 0 && !movieLocked}
          autoFocus
          placeholder="ابحث في قائمة الأفلام والمسلسلات…"
        />
        <ComboBox
          label="الممثل · اختياري ⭐ نقاط إضافية"
          options={options?.actors ?? []}
          value={actorGuess}
          onChange={setActorGuess}
          onSubmit={submit}
          locked={actorLocked}
          truthIfDone={done ? truth?.actor_name_ar : null}
          shake={shakeKey > 0 && !actorLocked}
          placeholder="ابحث في قائمة الممثلين… (سيبه فاضي لو مش عارف)"
        />
      </div>

      {!done ? (
        <div className="flex gap-2">
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="flex-1 py-3.5 rounded-md bg-amber-500 text-ink-900 font-bold text-base tracking-wide hover:bg-amber-300 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? '…' : 'خمّن'}
          </button>
          <button
            onClick={giveUp}
            className="px-5 py-3.5 rounded-md border border-ink-700 text-ink-300 text-sm hover:bg-ink-700/40 transition"
          >
            استسلم
          </button>
        </div>
      ) : (
        <div className="effeh-fade-up">
          <div className="text-center mb-5">
            <div className="text-5xl font-display text-ink-50 tabular-nums">
              {movieScore}<span className="text-xl text-ink-500"> / 100</span>
            </div>
            {bonusScore > 0 && (
              <div className="text-sm text-amber-300 mt-2 tabular-nums">
                ⭐ +{bonusScore} نقطة إضافية (الممثل)
              </div>
            )}
            <div className="mx-auto mt-3 w-16 effeh-rule opacity-60" />
            <div className="text-sm text-ink-300 mt-3">
              {totalScore === 150 ? 'صح كله مع البونس 👏'
                : movieScore === 100 ? 'الفيلم صح'
                : movieScore >= 60 ? 'حلو'
                : movieScore >= 20 ? 'مش بطّال'
                : outOfAttempts ? 'بكرة بإفيه أسهل' : '…'}
            </div>
          </div>

          {truth && (
            <div className="rounded-xl border border-ink-700/60 bg-ink-700/20 p-4 text-sm">
              <p>
                <span className="text-ink-500">الفيلم: </span>
                <span className="text-ink-50">
                  {truth.movie_title_ar}{' '}
                  <span className="text-ink-500">({truth.movie_year}) — {KIND_AR[truth.movie_kind] ?? truth.movie_kind}</span>
                </span>
              </p>
              <p className="mt-1">
                <span className="text-ink-500">الممثل: </span>
                <span className="text-ink-50">{truth.actor_name_ar}</span>
                {truth.character_name_ar && (
                  <span className="text-ink-500"> (بدور {truth.character_name_ar})</span>
                )}
              </p>
              {truth.source_url && (
                <p className="mt-2 text-xs">
                  <a href={truth.source_url} target="_blank" rel="noreferrer" className="text-amber-300 hover:underline">
                    المصدر ↗
                  </a>
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function numAr(n: number): string {
  const digits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']
  return String(n).split('').map((d) => digits[Number(d)] ?? d).join('')
}

export function attemptSquare(attempt: number | null): string {
  if (attempt == null) return '⬛'
  if (attempt === 1) return '🟩'
  if (attempt === 2) return '🟨'
  if (attempt === 3) return '🟧'
  return '🟥'
}
